import {
  MasonryEngineNodeAction,
  MasonryEngineActionHandler,
} from '@leagueplatform/masonry-engine';
import { MasonryDriverAction } from '@leagueplatform/masonry-renderers';
import {
  MasonryNodeAnalytics,
  postDataProcessor,
} from '@leagueplatform/masonry-api';
import { captureError } from '@leagueplatform/observability';
import {
  DataProcessorField,
  DataProcessorResponse,
} from 'types/data-processor.types';
import { getTranslation } from 'common/get-translation';
import { parseValidHttpsUrl } from 'common/parse-valid-https-url';
import { getBookAppointmentModalTargetPath } from 'utils/book-appointment-location-state';
import { getFieldValue } from 'utils/data-processor.utils';

export type MasonryBookAppointmentDataProcessorAction = MasonryEngineNodeAction<
  'invokeDataProcessor',
  {
    dataProcessorId: string;
    fields?: DataProcessorField[];
    suppressErrorModal?: boolean;
  },
  MasonryNodeAnalytics,
  DataProcessorResponse
>;

/**
 * Masonry action handler for booking an appointment. Calls the backend data
 * processor to obtain a booking link, validates it, and deep-links to the
 * current page with `bookingLink` in router state so the booking iframe modal
 * opens on top of the existing screen. Displays an error modal and reports to
 * observability on any failure.
 */
export const BookAppointmentDataProcessorActionHandler: MasonryEngineActionHandler<
  MasonryBookAppointmentDataProcessorAction,
  MasonryDriverAction
> = async (action, { invokeAction }) => {
  const { dataProcessorId, fields, suppressErrorModal } = action.payload;

  // Capture synchronously so a navigation that happens during the async data
  // processor call doesn't change the target path.
  const targetPath = getBookAppointmentModalTargetPath();

  const showErrorModal = (body: string) =>
    invokeAction({
      type: 'showModalWithProperties',
      namespace: 'default',
      ancestry: action.ancestry,
      event: action.event,
      payload: {
        heading: getTranslation('STR_ERROR_STATE_HEADING_GENERIC'),
        body,
        ctaLabel: getTranslation('STR_ERROR_STATE_CTA_DISMISS'),
      },
    });

  const handleFailure = async (
    logMessage: string,
    cause: unknown,
    response?: DataProcessorResponse,
  ) => {
    captureError(
      new Error(`BookAppointmentDataProcessorActionHandler: ${logMessage}`, {
        cause,
      }),
    );
    if (!suppressErrorModal) {
      await showErrorModal(
        response?.message || getTranslation('STR_ERROR_STATE_BODY_DEFAULT'),
      );
    }
    return {
      isSuccess: false as const,
      error: response?.errors || response?.message,
      data: response,
    };
  };

  let response: DataProcessorResponse;

  try {
    response = await postDataProcessor({ dataProcessorId, fields });
  } catch (error) {
    return handleFailure('Failed to invoke data processor', error);
  }

  if (response.status === 'error' || response.status === 'warning') {
    return handleFailure(
      `Data processor returned ${response.status}`,
      response.errors || response.message,
      response,
    );
  }

  const rawBookingLink = getFieldValue(response.fields, 'bookingLink');
  const bookingLink = parseValidHttpsUrl(rawBookingLink);

  if (!bookingLink) {
    return handleFailure(
      'Missing bookingLink in successful response',
      { bookingLink },
      response,
    );
  }

  try {
    await invokeAction({
      type: 'deeplink',
      namespace: 'default',
      ancestry: action.ancestry,
      event: action.event,
      payload: {
        url: targetPath,
        state: { bookingLink },
      },
    });
  } catch (navError) {
    return handleFailure('Failed to navigate to booking', navError, response);
  }

  return { isSuccess: true, data: response };
};
